import { z } from 'zod';

import type { ListParams } from '@/components/data/data-table';
import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { pageMetaSchema } from '@/lib/api/pagination';
import type { PageMeta } from '@/types/api';

import { toListQuery } from './filters';
import {
  bundleMetaPageSchema,
  customerMetaPageSchema,
  deviceMetaPageSchema,
  discountDetailSchema,
  discountListResponseSchema,
  ENTITY_ITEM_SCHEMA,
} from './schemas';
import type {
  ApplicableEntityType,
  BundleEntity,
  BundleMetaPage,
  CreateDiscountBody,
  CustomerEntity,
  CustomerMetaPage,
  DeviceEntity,
  DeviceMetaPage,
  DiscountDetail,
  DiscountListParams,
  DiscountListResponse,
  MetaParams,
} from './types';

/** Create a discount — the whole three-step wizard in one POST (grilling Q6). The 409/422 error
 *  codes are the source of truth (mapped to inline errors by the caller). Returns the new id. */
export async function createDiscount(body: CreateDiscountBody): Promise<{ id: string }> {
  return apiClient.post<{ id: string }>(endpoints.discounts.create, body);
}

export { toListQuery };

/** The Discount Name column's `dataIndex` is `name`, but the wire enum spells it `discountName`
 *  (`DiscountSortBy`) and a 400 VALIDATION_FAILED is the whole of ADO 117130. Every other
 *  sortable column's `dataIndex` already matches its enum member. Same arrangement as
 *  `admin-users`' and `reports/subscriptions`' `SORT_FIELD_MAP`. */
const SORT_FIELD_MAP: Record<string, string> = { name: 'discountName' };

// --- Async-multiselect meta pickers (ticket 08) ---
const metaQuery = ({ search, cursor, limit }: MetaParams) => ({
  params: { search, limit, ...(cursor ? { cursor } : {}) },
});

export async function listMetaCustomers(p: MetaParams): Promise<CustomerMetaPage> {
  // `status=active` keeps a disabled Customer out of the picker (ADO 117444). The wire param is
  // a single `AccountStatus`, not a list, so this also drops `pending_activation` Customers —
  // deliberate: neither can place an order, so neither can ever redeem the discount.
  const { params } = metaQuery(p);
  return customerMetaPageSchema.parse(
    await apiClient.getList(endpoints.discountsMeta.customers, {
      params: { ...params, status: 'active' },
    }),
  );
}
export async function listMetaBundles(p: MetaParams): Promise<BundleMetaPage> {
  return bundleMetaPageSchema.parse(
    await apiClient.getList(endpoints.discountsMeta.bundles, metaQuery(p)),
  );
}
export async function listMetaDevices(p: MetaParams): Promise<DeviceMetaPage> {
  // includeComponents=true selects the unified device+component list carrying the `isComponent`
  // flag the picker needs (the shared endpoint omits both without it — 109605).
  // `includeBundleOnlyDevices=false` drops a Device flagged "Not Available for Purchase
  // (bundle-only)" — it has no standalone listing to discount (ADO 115158). The BE default is
  // `true`, so this has to be sent explicitly. Components are never affected by the flag.
  const { params } = metaQuery(p);
  return deviceMetaPageSchema.parse(
    await apiClient.getList(endpoints.discountsMeta.devices, {
      params: { ...params, includeComponents: true, includeBundleOnlyDevices: false },
    }),
  );
}

// --- Discount domain API ---
/** Paginated / sorted / filtered discounts list. Defaults to Created Date descending (newest
 *  first) when no explicit sort is set (AC: default sort Created Date descending). Multi-selects
 *  join and only set params are sent via `toListQuery`. */
export async function listDiscounts(params: DiscountListParams): Promise<DiscountListResponse> {
  const { page, limit, sortBy, sortOrder, ...filters } = params;
  const { data, meta } = await apiClient.getList(endpoints.discounts.list, {
    params: {
      page,
      limit,
      sortBy: sortBy ? (SORT_FIELD_MAP[sortBy] ?? sortBy) : 'createdDate',
      sortOrder: sortOrder ?? 'desc',
      ...toListQuery(filters),
    },
  });
  return discountListResponseSchema.parse({ data, meta });
}

/** Enable/disable a discount. Fire-and-forget on the FE — the mutation invalidates the
 *  listing, which refetches the new status (AC: chip flips on success). An expired discount
 *  returns 422 DISCOUNT_EXPIRED (guarded by the Expired-only menu; surfaced defensively). */
export async function setDiscountStatus(id: string, status: 'active' | 'inactive'): Promise<void> {
  await apiClient.patch(endpoints.discounts.status(id), { status });
}

/** Permanently delete a discount (Active / Inactive / Expired). 204 on success. */
export async function deleteDiscount(id: string): Promise<void> {
  await apiClient.delete(endpoints.discounts.detail(id));
}

/** Full discount config for the detail page. 404 → ApiError `DISCOUNT_NOT_FOUND`. */
export async function getDiscount(id: string): Promise<DiscountDetail> {
  return discountDetailSchema.parse(await apiClient.get(endpoints.discounts.detail(id)));
}

/** Row shape per relationship tab (ticket 06). Keyed by the `type` param so the return type
 *  narrows to the requested entity. */
type EntityByType = { customer: CustomerEntity; device: DeviceEntity; bundle: BundleEntity };

/** One page of a discount's applicable entities for the relationship tabs (Customers / Devices /
 *  Bundles). `type` selects both the query filter and the per-item validator; the offset-paginated
 *  `{ data, meta }` envelope is validated against the entity schema + `pageMetaSchema`. */
export async function getApplicableEntities<T extends ApplicableEntityType>(
  id: string,
  type: T,
  params: ListParams<{ search?: string }>,
): Promise<{ data: EntityByType[T][]; meta: PageMeta }> {
  const { data, meta } = await apiClient.getList(endpoints.discounts.applicableEntities(id), {
    params: { type, ...params },
  });
  const items = z.array(ENTITY_ITEM_SCHEMA[type]).parse(data) as EntityByType[T][];
  return { data: items, meta: pageMetaSchema.parse(meta) };
}

// --- Create-wizard API (tickets 109605/606/607) ---
/** Step-1 "Next" uniqueness gate. The final 409 at Create remains the source of truth. */
export async function checkDiscountNameAvailability(name: string): Promise<boolean> {
  const { available } = await apiClient.get<{ available: boolean }>(
    endpoints.discounts.nameAvailability,
    { params: { name: name.trim() } },
  );
  return available;
}
